import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Image as ImageIcon,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

const PhotoGallery = () => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [itemsPerView, setItemsPerView] = useState(4);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [touchState, setTouchState] = useState({
    startX: 0,
    startY: 0,
    initialDistance: 0,
    isMoving: false
  });

  const imageContainerRef = useRef(null);
  const imageRef = useRef(null);

  // Image list
  const images = Array.from({ length: 21 }).map((_, index) => ({
    src: `/assets/images/photos/photos_${index + 1}.jpeg`,
    alt: `Wedding Photo ${index + 1}`
  }));

  // Responsive configuration
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) setItemsPerView(1);        // Mobile
      else if (width < 1024) setItemsPerView(2);  // Tablet
      else if (width < 1280) setItemsPerView(3);  // Small Desktop
      else setItemsPerView(4);                    // Large Desktop
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Enhanced zoom and pan logic
  const handleZoom = useCallback((deltaOrScale, pointX, pointY) => {
    if (!isLightboxOpen || !imageContainerRef.current) return;

    const rect = imageContainerRef.current.getBoundingClientRect();
    const containerWidth = rect.width;
    const containerHeight = rect.height;

    const newZoom = typeof deltaOrScale === 'number'
      ? Math.max(1, Math.min(3, zoomLevel * deltaOrScale))
      : deltaOrScale;

    // Calculate pan offset based on zoom point
    const relativeX = (pointX - rect.left) / containerWidth;
    const relativeY = (pointY - rect.top) / containerHeight;

    const zoomFactor = newZoom - zoomLevel;
    const newX = imagePosition.x - zoomFactor * containerWidth * (relativeX - 0.5);
    const newY = imagePosition.y - zoomFactor * containerHeight * (relativeY - 0.5);

    // Limit pan movement
    const maxMoveX = containerWidth * (newZoom - 1) / 2;
    const maxMoveY = containerHeight * (newZoom - 1) / 2;

    const boundedX = Math.max(-maxMoveX, Math.min(maxMoveX, newX));
    const boundedY = Math.max(-maxMoveY, Math.min(maxMoveY, newY));

    setZoomLevel(newZoom);
    setImagePosition({ x: boundedX, y: boundedY });
  }, [isLightboxOpen, zoomLevel, imagePosition]);

  // Mouse wheel zoom
  const handleWheel = useCallback((e) => {
    if (!isLightboxOpen) return;

    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    handleZoom(delta, e.clientX, e.clientY);
  }, [handleZoom, isLightboxOpen]);

  // Mouse pan
  const handleMouseDown = useCallback((e) => {
    if (zoomLevel <= 1) return;

    const startX = e.clientX - imagePosition.x;
    const startY = e.clientY - imagePosition.y;

    const handleMouseMove = (moveEvent) => {
      if (imageContainerRef.current) {
        const rect = imageContainerRef.current.getBoundingClientRect();
        const maxMoveX = rect.width * (zoomLevel - 1) / 2;
        const maxMoveY = rect.height * (zoomLevel - 1) / 2;

        const newX = Math.max(-maxMoveX, Math.min(maxMoveX, moveEvent.clientX - startX));
        const newY = Math.max(-maxMoveY, Math.min(maxMoveY, moveEvent.clientY - startY));

        setImagePosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [zoomLevel, imagePosition]);

  // Touch interactions
  const handleTouchStart = useCallback((e) => {
    if (!isLightboxOpen || zoomLevel <= 1) return;

    if (e.touches.length === 2) {
      // Pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const initialDistance = Math.hypot(
        touch1.pageX - touch2.pageX,
        touch1.pageY - touch2.pageY
      );

      setTouchState(prev => ({
        ...prev,
        initialDistance,
        isMoving: true
      }));
    } else if (e.touches.length === 1) {
      // Pan
      setTouchState({
        startX: e.touches[0].clientX - imagePosition.x,
        startY: e.touches[0].clientY - imagePosition.y,
        isMoving: true
      });
    }
  }, [isLightboxOpen, zoomLevel, imagePosition]);

  const handleTouchMove = useCallback((e) => {
    if (!isLightboxOpen || !touchState.isMoving) return;

    if (e.touches.length === 2 && touchState.initialDistance) {
      // Pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.hypot(
        touch1.pageX - touch2.pageX,
        touch1.pageY - touch2.pageY
      );

      const centerX = (touch1.pageX + touch2.pageX) / 2;
      const centerY = (touch1.pageY + touch2.pageY) / 2;

      const zoomScale = currentDistance / touchState.initialDistance;
      handleZoom(zoomScale, centerX, centerY);

    } else if (e.touches.length === 1 && zoomLevel > 1) {
      // Pan
      if (imageContainerRef.current) {
        const rect = imageContainerRef.current.getBoundingClientRect();
        const maxMoveX = rect.width * (zoomLevel - 1) / 2;
        const maxMoveY = rect.height * (zoomLevel - 1) / 2;

        const touch = e.touches[0];
        const newX = Math.max(-maxMoveX, Math.min(maxMoveX, touch.clientX - touchState.startX));
        const newY = Math.max(-maxMoveY, Math.min(maxMoveY, touch.clientY - touchState.startY));

        setImagePosition({ x: newX, y: newY });
      }
    }
  }, [isLightboxOpen, touchState, zoomLevel, handleZoom]);

  const handleTouchEnd = useCallback(() => {
    setTouchState({
      startX: 0,
      startY: 0,
      initialDistance: 0,
      isMoving: false
    });
  }, []);

  // Other methods remain the same as in the previous implementation
  const nextImage = () => {
    if (currentImageIndex < images.length - 1) {
      setCurrentImageIndex(prev => prev + 1);
      setZoomLevel(1);
      setImagePosition({ x: 0, y: 0 });
    }
  };

  const prevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1);
      setZoomLevel(1);
      setImagePosition({ x: 0, y: 0 });
    }
  };

  const zoomIn = () => handleZoom(1.5, window.innerWidth / 2, window.innerHeight / 2);
  const zoomOut = () => handleZoom(0.667, window.innerWidth / 2, window.innerHeight / 2);

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  // Add event listeners for touch and zoom
  useEffect(() => {
    if (isLightboxOpen) {
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isLightboxOpen, handleTouchMove, handleTouchEnd]);

  // Keyboard and swipe navigation
  const handleKeyDown = useCallback((e) => {
    if (!isLightboxOpen) return;

    switch (e.key) {
      case 'ArrowRight':
        nextImage();
        break;
      case 'ArrowLeft':
        prevImage();
        break;
      case 'Escape':
        closeLightbox();
        break;
      case '+':
        zoomIn();
        break;
      case '-':
        zoomOut();
        break;
      default:
        break;
    }
  }, [isLightboxOpen, nextImage, prevImage, zoomIn, zoomOut]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);


  const nextCarousel = () => {
    if (carouselIndex < totalPages - 1) {
      setCarouselIndex(prev => prev + 1);
    }
  };

  const prevCarousel = () => {
    if (carouselIndex > 0) {
      setCarouselIndex(prev => prev - 1);
    }
  };

  const totalPages = Math.ceil(images.length / itemsPerView);
  const visibleImages = images.slice(
    carouselIndex * itemsPerView,
    (carouselIndex + 1) * itemsPerView
  );

  return (
    <section
      className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 
      bg-gradient-to-b from-rose-50/50 via-white to-rose-50/50 
      min-h-screen flex items-center"
    >
      <div className="w-full max-w-7xl mx-auto">
        {/* Page Title */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <ImageIcon
            className="text-rose-400 text-4xl sm:text-5xl mx-auto mb-6 
            animate-pulse hover:animate-spin"
          />
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-gray-800 tracking-wide">
            Album Ảnh Cưới
          </h2>
        </div>

        {/* Image Gallery */}
        <div className="relative px-4 sm:px-8 lg:px-12">
          {/* Navigation Buttons */}
          {carouselIndex > 0 && (
            <button
              onClick={prevCarousel}
              className="absolute -left-4 sm:-left-8 top-1/2 -translate-y-1/2 
              z-10 bg-white/90 p-3 rounded-full shadow-lg 
              hover:bg-rose-50 transition-all duration-300 
              focus:outline-none focus:ring-2 focus:ring-rose-300"
            >
              <ChevronLeft className="w-6 h-6 text-gray-800" />
            </button>
          )}

          {carouselIndex < totalPages - 1 && (
            <button
              onClick={nextCarousel}
              className="absolute -right-4 sm:-right-8 top-1/2 -translate-y-1/2 
              z-10 bg-white/90 p-3 rounded-full shadow-lg 
              hover:bg-rose-50 transition-all duration-300 
              focus:outline-none focus:ring-2 focus:ring-rose-300"
            >
              <ChevronRight className="w-6 h-6 text-gray-800" />
            </button>
          )}

          {/* Image Grid */}
          <motion.div
            className="grid gap-4 sm:gap-6 lg:gap-8"
            style={{
              gridTemplateColumns: `repeat(${itemsPerView}, minmax(0, 1fr))`,
            }}
          >
            {visibleImages.map((image, index) => (
              <motion.div
                key={index + carouselIndex * itemsPerView}
                className="aspect-[4/5] rounded-2xl overflow-hidden 
                shadow-lg relative group cursor-pointer"
                onClick={() => {
                  setCurrentImageIndex(index + carouselIndex * itemsPerView);
                  setIsLightboxOpen(true);
                }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="relative group w-full h-full overflow-hidden">
                  <LazyLoadImage
                    src={image.src}
                    alt={image.alt}
                    effect="blur"
                    className="w-full h-full object-cover transition-all duration-500 
    group-hover:scale-110 filter blur-[2px] group-hover:blur-0"
                    wrapperClassName="w-full h-full"
                  />
                </div>
                <div className="absolute inset-0 
                bg-gradient-to-t from-black/50 via-transparent to-transparent 
                opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </motion.div>

          {/* Pagination Dots */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-3 mt-8">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCarouselIndex(index)}
                  className={`transition-all duration-300 focus:outline-none ${carouselIndex === index
                    ? 'w-8 h-2 bg-rose-500'
                    : 'w-2 h-2 bg-rose-200 hover:bg-rose-300'
                    } rounded-full`}
                  aria-label={`Go to page ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Lightbox */}
        <AnimatePresence>
          {isLightboxOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/95 z-50 
            flex items-center justify-center"
            >
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 
                text-white/70 hover:text-white z-10 p-2"
                aria-label="Close lightbox"
              >
                <X className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>

              {/* Previous/Next Navigation */}
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 
              flex items-center justify-between px-4 sm:px-8">
                {currentImageIndex > 0 && (
                  <button
                    onClick={prevImage}
                    className="p-2 text-white/70 hover:text-white 
                    transition-colors"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-8 h-8 sm:w-10 sm:h-10" />
                  </button>
                )}

                {currentImageIndex < images.length - 1 && (
                  <button
                    onClick={nextImage}
                    className="p-2 text-white/70 hover:text-white 
                    transition-colors"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-8 h-8 sm:w-10 sm:h-10" />
                  </button>
                )}
              </div>

              {/* Zoom Controls */}
              <div className="absolute top-4 left-4 sm:top-6 sm:left-6 
              flex space-x-2 z-10">
                <button
                  onClick={zoomOut}
                  disabled={zoomLevel === 1}
                  className="p-2 text-white/70 hover:text-white 
                  disabled:opacity-30 transition-colors"
                  aria-label="Zoom out"
                >
                  <ZoomOut className="w-6 h-6 sm:w-8 sm:h-8" />
                </button>
                <button
                  onClick={zoomIn}
                  disabled={zoomLevel === 3}
                  className="p-2 text-white/70 hover:text-white 
                  disabled:opacity-30 transition-colors"
                  aria-label="Zoom in"
                >
                  <ZoomIn className="w-6 h-6 sm:w-8 sm:h-8" />
                </button>
              </div>

              {/* Image Display */}
              <motion.div
                key={currentImageIndex}
                className="w-full h-full p-4 sm:p-8 flex items-center justify-center"
                ref={imageContainerRef}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
              >
                <div
                  style={{
                    transform: `scale(${zoomLevel}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                    transition: 'transform 0.3s ease-in-out',
                    cursor: zoomLevel > 1 ? 'move' : 'default'
                  }}
                  className="max-w-full max-h-[85vh] flex items-center justify-center"
                  ref={imageRef}
                >
                  <LazyLoadImage
                    src={images[currentImageIndex].src}
                    alt={images[currentImageIndex].alt}
                    effect="blur"
                    className="max-w-full max-h-[85vh] object-cover rounded-lg"
                  />
                </div>
              </motion.div>

              {/* Image Counter */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 
              text-white/90 font-medium tracking-wide">
                {currentImageIndex + 1} / {images.length}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default PhotoGallery;